import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Side drawer on `@radix-ui/react-dialog`. Opens from the end edge (right in LTR, left in
 * RTL) and is 640px wide on desktop, full width on a phone. Based on `ui/dialog.tsx`:
 * clicks inside a Select / Popover / Dropdown portal never count as "outside".
 */
const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Overlay
        ref={ref}
        className={cn(
            'fixed inset-0 z-50 bg-foreground/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            className
        )}
        {...props}
    />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const isNestedOverlayTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) {
        return false;
    }

    return Boolean(
        target.closest('[data-radix-popover-content]') ||
            target.closest('[data-radix-select-content]') ||
            target.closest('[data-radix-dropdown-menu-content]') ||
            target.closest('[role="alertdialog"]') ||
            target.closest('[cmdk-root]')
    );
};

interface SheetContentProps extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> {
    /** Keep the drawer open on overlay clicks (default) so an unsaved form is never lost. */
    preventOutsideClick?: boolean;
    /** Label of the built-in close button (screen readers). */
    closeLabel?: string;
}

const SheetContent = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Content>,
    SheetContentProps
>(({ className, children, preventOutsideClick = true, closeLabel = 'Close', ...props }, ref) => (
    <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
            ref={ref}
            className={cn(
                'fixed inset-y-0 end-0 z-50 flex h-full w-full flex-col border-s border-border bg-card text-card-foreground shadow-xl outline-none sm:max-w-[640px]',
                'duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right rtl:data-[state=closed]:slide-out-to-left rtl:data-[state=open]:slide-in-from-left',
                className
            )}
            onPointerDownOutside={event => {
                if (preventOutsideClick || isNestedOverlayTarget(event.target)) {
                    event.preventDefault();
                }
            }}
            onInteractOutside={event => {
                if (preventOutsideClick || isNestedOverlayTarget(event.target)) {
                    event.preventDefault();
                }
            }}
            onFocusOutside={event => {
                if (isNestedOverlayTarget(event.target)) {
                    event.preventDefault();
                }
            }}
            {...props}
        >
            {children}
            <SheetPrimitive.Close className="absolute end-4 top-4 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none">
                <X className="h-4 w-4" />
                <span className="sr-only">{closeLabel}</span>
            </SheetPrimitive.Close>
        </SheetPrimitive.Content>
    </SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            'flex flex-shrink-0 flex-col gap-1 border-b border-border px-6 py-5',
            className
        )}
        {...props}
    />
);
SheetHeader.displayName = 'SheetHeader';

const SheetBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-5', className)} {...props} />
);
SheetBody.displayName = 'SheetBody';

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            'flex flex-shrink-0 flex-wrap items-center gap-2 border-t border-border bg-card px-6 py-4',
            className
        )}
        {...props}
    />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Title
        ref={ref}
        className={cn('text-lg font-semibold leading-none tracking-tight', className)}
        {...props}
    />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
    React.ElementRef<typeof SheetPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
    <SheetPrimitive.Description
        ref={ref}
        className={cn('text-sm text-muted-foreground', className)}
        {...props}
    />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
    Sheet,
    SheetPortal,
    SheetOverlay,
    SheetClose,
    SheetTrigger,
    SheetContent,
    SheetHeader,
    SheetBody,
    SheetFooter,
    SheetTitle,
    SheetDescription,
};
